import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, CreditCard, FileSignature, Sparkles, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const ICONS = { invoice_paid: CreditCard, quote_accepted: FileSignature, test: Sparkles, ct_near: Landmark, ct_over: Landmark };

function b64ToUint8(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function enablePush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return "unsupported";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return "denied";
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  const sub = (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) }));
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return "ok";
}

export default function NotificationsBell() {
  const { t, locale } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [pushState, setPushState] = useState(() => (typeof Notification !== "undefined" ? Notification.permission : "unsupported"));
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 45000); return () => clearInterval(id); }, []);
  useEffect(() => {
    if (pushState === "granted" && "serviceWorker" in navigator) enablePush().catch(() => {});
  }, [pushState]);

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => {
    if (!n.read) api.post(`/notifications/${n.id}/read`).then(load);
    setOpen(false);
    nav(n.url);
  };
  const onEnable = async () => {
    try {
      const r = await enablePush();
      if (r === "ok") { setPushState("granted"); toast.success(t("pushEnabled")); }
      else toast.error(t(r === "denied" ? "pushDenied" : "pushUnsupported"));
    } catch { toast.error(t("pushUnsupported")); }
  };
  const Icon = data.unread ? BellRing : Bell;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">
          <Icon className="h-5 w-5" />
          {data.unread > 0 && <span data-testid="notifications-unread-badge" className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#CE1126] px-1 text-[10px] font-bold text-white">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h4 className="font-display text-sm font-bold">{t("notifications")}</h4>
          {data.unread > 0 && <button data-testid="notifications-read-all" onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-[#007A3D] hover:underline"><CheckCheck className="h-3.5 w-3.5" />{t("markAllRead")}</button>}
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {data.items.length === 0 && <p data-testid="notifications-empty" className="px-4 py-8 text-center text-sm text-slate-400">{t("noNotifications")}</p>}
          {data.items.map((n) => { const I = ICONS[n.kind] || Bell; return (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => openItem(n)} className={`flex w-full gap-3 px-4 py-3 text-start transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-emerald-50/60"}`}>
              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-100 text-slate-500" : "bg-[#007A3D] text-white"}`}><I className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-slate-900">{n.title}</span>
                <span className="block text-xs text-slate-600">{n.body}</span>
                <span className="mt-1 block font-mono text-[10px] text-slate-400">{new Date(n.created_at).toLocaleString(locale)}</span>
              </span>
            </button>
          ); })}
        </div>
        {pushState !== "granted" && (
          <div className="border-t border-slate-100 p-3">
            <Button data-testid="enable-push-btn" onClick={onEnable} size="sm" className="w-full bg-[#0B1220] text-white hover:bg-slate-800"><BellRing className="me-1 h-4 w-4 text-[#D4AF37]" />{t("enablePush")}</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
